import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import * as IAction from 'app/interfaces/actiontracker.interface';
import {
	FeedNotification,
	FeedNotificationResponse,
} from 'app/interfaces/feed-notifications.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { FeedNotificationService } from 'app/services/feed-notifications.service';
import { Subscription } from 'rxjs';

@Component({
	selector: 'notifications-card',
	templateUrl: './notifications-card.component.html',
	styleUrls: ['./notifications-card.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class NotificationsCardComponent implements OnChanges, OnDestroy {
	@Input()
	public isOpen = false;

	@Input()
	public hasNewNotifications = false;

	@Output()
	protected onClose: EventEmitter<Event> = new EventEmitter();

	@Output()
	protected onNotificationSeen: EventEmitter<NotificationSeenEvent> = new EventEmitter<NotificationSeenEvent>();

	@ViewChild('card', { read: ElementRef })
	protected cardReference: ElementRef;

	@ViewChild('closeCard', { read: ElementRef })
	protected closeCardReference: ElementRef;

	protected notifications: FeedNotification[] = [];
	protected hasErrors = false;
	protected isLoadingMoreNotifications = false;
	protected hasLoadedNotifications = false;
	protected getNotificationsObservable: Subscription;
	protected totalNotifications = 0;

	constructor(
		private feedNotificationService: FeedNotificationService,
		private actionTrackerService: ActionTrackerService
	) {}

	ngOnChanges(): void {
		if (this.isOpen && this.shouldTriggerGetNotitifications()) {
			if (!this.hasLoadedNotifications) {
				this.actionTrackerService.eventTracker(
					IAction.SCOPE_TYPE.Office,
					IAction.SUBSCOPE_TYPE.Notification,
					IAction.ITEM_TYPE.FeedScreen,
					IAction.INTERACTION_TYPE.Click
				);
			}

			this.getNotificationsObservable = this.feedNotificationService
				.getFeedNotifications(0, 4)
				.subscribe(
					(result: FeedNotificationResponse) =>
						this.processNotifications(result),
					() => (this.hasErrors = true)
				);
		}
	}

	ngOnDestroy(): void {
		if (
			this.getNotificationsObservable &&
			!this.getNotificationsObservable.closed
		) {
			this.getNotificationsObservable.unsubscribe();
		}
	}

	private shouldTriggerGetNotitifications(): boolean {
		return !this.hasLoadedNotifications || this.hasNewNotifications;
	}

	private processNotifications(
		notificationsResponse: FeedNotificationResponse
	): void {
		if (!notificationsResponse.data) {
			return;
		}

		this.notifications = notificationsResponse.data;
		this.totalNotifications = notificationsResponse.total;
		this.hasLoadedNotifications = true;

		if (notificationsResponse.data.length > 0) {
			this.setNotificationsAsSeen(notificationsResponse.data);
		}
	}

	private setNotificationsAsSeen(notifications: FeedNotification[]): void {
		if (!notifications) {
			return;
		}

		const notificationsNotSeen = notifications.filter(
			(notification) => !notification.seen
		);

		const notificationsId = this.trackNotificationsSeen(
			notificationsNotSeen
		).map((notification) => notification.id);

		if (notificationsId.length > 0) {
			this.feedNotificationService
				.setNotificationsAsSeen(notificationsId)
				.subscribe(() =>
					this.onNotificationSeen.emit({ total: notificationsId.length })
				);
		}
	}

	private trackNotificationsSeen(
		notifications: FeedNotification[]
	): FeedNotification[] {
		notifications.forEach((notification) => {
			this.actionTrackerService.eventTracker(
				IAction.SCOPE_TYPE.Office,
				IAction.SUBSCOPE_TYPE.Notification,
				IAction.ITEM_TYPE.FeedCard,
				IAction.INTERACTION_TYPE.Seen,
				{
					notificationType: notification.actionTracker,
				}
			);
		});

		return notifications;
	}

	loadMoreNotifications(): void {
		this.isLoadingMoreNotifications = true;
		this.feedNotificationService
			.getFeedNotifications(this.notifications.length, 2)
			.subscribe(
				(result: FeedNotificationResponse) =>
					this.processLoadMoreNotifications(result.data),
				() => (this.hasErrors = true)
			);
	}

	private processLoadMoreNotifications(
		notificationsResponse: FeedNotification[]
	): void {
		if (notificationsResponse.length === 0) {
			this.isLoadingMoreNotifications = false;
			return;
		}

		this.notifications.push(...notificationsResponse);
		this.setNotificationsAsSeen(notificationsResponse);

		this.isLoadingMoreNotifications = false;
	}

	hideNotification(id: number): void {
		this.notifications = this.notifications.filter((n) => n.id !== id);
	}

	hasNotifications(): boolean {
		return this.notifications.length > 0;
	}

	@HostListener('document:mousedown', ['$event'])
	handleClickEvent(event: Event): void {
		if (this.eventTargetIsCard(event.target as HTMLElement)) {
			event.preventDefault();
			return;
		}

		if (this.shouldCloseCard(event)) {
			this.onClose.emit();
		}
	}

	private eventTargetIsCard(target: HTMLElement): boolean {
		return target.classList.contains('p-card-content');
	}

	private shouldCloseCard(event: Event): boolean {
		return (
			!this.cardReference?.nativeElement.contains(event.target) ||
			this.closeCardReference?.nativeElement.contains(event.target)
		);
	}
}

export interface NotificationSeenEvent {
	total: number;
}
